'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CultureHighlight {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
}

interface CultureShowcaseProps {
  className?: string;
}

const CultureShowcase = ({ className = '' }: CultureShowcaseProps) => {
  const [activeHighlight, setActiveHighlight] = useState<number>(1);

  const highlights: CultureHighlight[] = [
  {
    id: 1,
    title: 'Continuous Learning',
    description: 'We invest in our team\'s growth with dedicated learning budgets, certification programs, and access to cutting-edge technologies. Every team member receives annual training allowances and time for skill development.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_171170bd3-1764687877901.png",
    alt: 'Diverse team of professionals collaborating around laptop in modern office with natural lighting',
    icon: 'AcademicCapIcon'
  },
  {
    id: 2,
    title: 'Work-Life Balance',
    description: 'Flexible work arrangements, remote-first culture, and unlimited PTO ensure our team stays energized and productive. We believe great work happens when people have time to recharge.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac8c895a-1764761582414.png",
    alt: 'Happy team members in casual attire having discussion in bright collaborative workspace',
    icon: 'ScaleIcon'
  },
  {
    id: 3,
    title: 'Innovation Culture',
    description: '20% time for passion projects, hackathons, and innovation labs where team members explore new technologies and contribute to open-source projects. Your ideas shape our future.',
    image: "https://images.unsplash.com/photo-1590402494562-4b788beb429e",
    alt: 'Creative team brainstorming with sticky notes on glass wall in modern tech office',
    icon: 'LightBulbIcon'
  },
  {
    id: 4,
    title: 'Diverse & Inclusive',
    description: 'We celebrate diversity in all forms and foster an inclusive environment where every voice is heard. Our team spans multiple countries, cultures, and backgrounds, making us stronger together.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecddc1a5-1767033646130.png",
    alt: 'Multicultural team of professionals smiling together in contemporary office environment',
    icon: 'UserGroupIcon'
  }];


  const activeItem = highlights.find((h) => h.id === activeHighlight) || highlights[0];

  return (
    <section className={`py-16 md:py-20 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold text-primary mb-4">
              Our Culture
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A workplace where technical excellence meets human connection. We build not just great solutions, but great teams.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <div className="space-y-4">
                {highlights.map((highlight) =>
                <button
                  key={highlight.id}
                  onClick={() => setActiveHighlight(highlight.id)}
                  className={`w-full p-6 rounded-lg border-2 transition-all duration-base text-left ${
                  activeHighlight === highlight.id ?
                  'border-primary bg-card shadow-md' :
                  'border-transparent bg-card/50 hover:border-primary/30 hover:bg-card'}`
                  }>

                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-base ${
                    activeHighlight === highlight.id ?
                    'bg-primary text-primary-foreground' :
                    'bg-primary/10 text-primary'}`
                    }>
                        <Icon name={highlight.icon as any} size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-headline font-bold text-foreground mb-2">
                          {highlight.title}
                        </h3>
                        <p className={`text-sm text-muted-foreground leading-relaxed transition-all duration-base ${
                      activeHighlight === highlight.id ? 'opacity-100' : 'opacity-70'}`
                      }>
                          {highlight.description}
                        </p>
                      </div>
                      <Icon
                      name={activeHighlight === highlight.id ? 'ChevronRightIcon' : 'ChevronDownIcon'}
                      size={20}
                      className="text-muted-foreground flex-shrink-0" />

                    </div>
                  </button>
                )}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative rounded-lg overflow-hidden shadow-lg aspect-[4/3]">
                <AppImage
                  src={activeItem.image}
                  alt={activeItem.alt}
                  fill
                  className="object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <Icon name={activeItem.icon as any} size={20} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-headline font-bold text-white">
                      {activeItem.title}
                    </h3>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">
                    {activeItem.description}
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-card rounded-lg p-4 text-center shadow-sm">
                  <p className="text-2xl font-headline font-bold text-primary mb-1">4.8/5</p>
                  <p className="text-xs text-muted-foreground">Employee Rating</p>
                </div>
                <div className="bg-card rounded-lg p-4 text-center shadow-sm">
                  <p className="text-2xl font-headline font-bold text-secondary mb-1">95%</p>
                  <p className="text-xs text-muted-foreground">Retention Rate</p>
                </div>
                <div className="bg-card rounded-lg p-4 text-center shadow-sm">
                  <p className="text-2xl font-headline font-bold text-accent mb-1">40+</p>
                  <p className="text-xs text-muted-foreground">Team Members</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CultureShowcase;